/**
 * "Name the Team Manager" — the one control that answers §10's refusal.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS AS ITS OWN COMPONENT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §10 addresses the Hearing Letter to the Team Manager by name, and
 * `generateHearingLetter` refuses a case that names none — correctly, and that refusal is not
 * touched here. What was missing was the answer to it. A Technical Delegate created a case,
 * pressed **Draft Hearing Letter**, was told *"the Hearing Letter is addressed to the Team
 * Manager, and this case does not name one"*, and had nowhere on the page to name one.
 *
 * So the control appears in the two places a TD meets that fact: on the **Case File**, where
 * the case's own head grid says who the letter will be addressed to, and on **Batch 1**, at
 * the point of refusal, because sending him to another panel on a match day is how a hearing
 * starts late. Both are the same component writing through the same route; each draws only
 * while the case names nobody, and both vanish the moment one is named.
 *
 * **The create form is the real fix, and this is the remedy for the cases that pre-date it.**
 * `CreateHearingModal` now asks for the Team Manager and pre-fills it from the team list, so
 * a case opened today arrives with one. This is how a case opened before that gets one
 * without being recreated — which for a disciplinary record would mean a new case number on
 * the register and the old one abandoned.
 */

import { useState } from "react";

import { putHearingTeamManager } from "../../api/client.js";

/** Said wherever the case names nobody, in the words the API's own refusal uses. */
export const TEAM_MANAGER_MISSING =
  "The Hearing Letter is addressed to the Team Manager, and this case does not name one.";

export interface TeamManagerFieldProps {
  caseId: string;
  /** Where this instance is drawn — testids and the label differ, the behaviour does not. */
  place: "caseFile" | "batch1";
  /** True while the surrounding panel has a write of its own in flight. */
  busy?: boolean;
  /** Called after the name has been stored, so the page re-reads the case. */
  onSaved: () => void;
}

export function TeamManagerField({
  caseId,
  place,
  busy = false,
  onSaved,
}: TeamManagerFieldProps): React.JSX.Element {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const save = (): void => {
    const name = draft.trim();
    if (name === "") {
      setFailure("Type the Team Manager's name — the letter is addressed to them by name.");
      return;
    }
    setFailure(null);
    setSaving(true);
    putHearingTeamManager(caseId, name).then(
      () => {
        setSaving(false);
        setDraft("");
        onSaved();
      },
      (cause: unknown) => {
        setSaving(false);
        setFailure(cause instanceof Error ? cause.message : "The request failed.");
      },
    );
  };

  return (
    <div className="field tmfield" data-testid={`teamManagerFix-${place}`}>
      <span className="field__label">Name The Team Manager</span>
      <span className="field__sub">{TEAM_MANAGER_MISSING}</span>
      <input
        type="text"
        aria-label="Team Manager name"
        placeholder="Team Manager name"
        autoComplete="off"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
      />
      <button
        type="button"
        className="gbtn gbtn--sm"
        disabled={busy || saving}
        onClick={save}
        data-testid={`teamManagerSave-${place}`}
      >
        {saving ? "Working…" : "Save Team Manager"}
      </button>
      {failure === null ? null : (
        <span className="field__bad" role="alert" data-testid={`teamManagerFail-${place}`}>
          {failure}
        </span>
      )}
    </div>
  );
}
